import { z } from 'zod';
import {
  RATE_MODE_OPTIONS,
  rateModeDefaults,
  rateModeFields,
  refineRateMode,
} from '../_shared';

export { RATE_MODE_OPTIONS };

export const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Masculino' },
  { value: 'FEMALE', label: 'Femenino' },
  { value: 'OTHER', label: 'Otro' },
] as const;

export const APV_REGIME_OPTIONS = [
  { value: 'A', label: 'Régimen A' },
  { value: 'B', label: 'Régimen B' },
] as const;

export const SAVINGS_OPTION_OPTIONS = [
  { value: 'savings_capacity', label: 'Capacidad de ahorro' },
  { value: 'ideal_pension', label: 'Pensión ideal' },
] as const;

export const PRIME_PERIODICITY_OPTIONS = [
  { value: 'MONTHLY', label: 'Mensual' },
  { value: 'QUARTERLY', label: 'Trimestral' },
  { value: 'SEMIANNUAL', label: 'Semestral' },
  { value: 'ANNUAL', label: 'Anual' },
] as const;

const sharedFields = {
  date_of_birth: z
    .string()
    .min(1, 'Requerido')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Formato YYYY-MM-DD'),
  gender: z.enum(['MALE', 'FEMALE', 'OTHER']),
  net_salary: z.coerce.number().nonnegative('Debe ser ≥ 0'),
  afp_balance: z.coerce.number().nonnegative('Debe ser ≥ 0'),
  ...rateModeFields,
  expected_retirement: z.coerce.number().int().positive('Debe ser > 0'),
  apv_regime: z.enum(['A', 'B']),
  savings_option: z.enum(['savings_capacity', 'ideal_pension']),
  payment_periodicity: z.enum(['MONTHLY', 'QUARTERLY', 'SEMIANNUAL', 'ANNUAL']),
  apv_transfer_amount: z.coerce.number().nonnegative(),
  initial_contribution: z.coerce.number().nonnegative(),
  initial_agreed_deposit: z.coerce.number().nonnegative(),
  annual_direct_contribution: z.coerce.number().nonnegative(),
  annual_agreed_deposits: z.coerce.number().nonnegative(),
  death_insured_capital: z.coerce.number().nonnegative(),
  accidental_death_insured_capital: z.coerce.number().nonnegative(),
  itp_insured_capital: z.coerce.number().nonnegative(),
};

export const apvSelectSchema = z.discriminatedUnion('savings_option', [
  z.object({
    ...sharedFields,
    savings_option: z.literal('savings_capacity'),
    agreed_premium: z.coerce.number().nonnegative(),
  }),
  z.object({
    ...sharedFields,
    savings_option: z.literal('ideal_pension'),
    ideal_pension: z.coerce.number().nonnegative(),
  }),
]).superRefine(refineRateMode);

export type APVSelectFormValues = z.infer<typeof apvSelectSchema>;

const sharedDefaults = {
  date_of_birth: '1990-01-01',
  gender: 'MALE' as const,
  net_salary: 50,
  afp_balance: 750,
  ...rateModeDefaults,
  projection_rate: 0.03,
  expected_retirement: 65,
  apv_regime: 'A' as const,
  apv_transfer_amount: 600,
  initial_contribution: 0,
  initial_agreed_deposit: 0,
  annual_direct_contribution: 0,
  annual_agreed_deposits: 0,
  death_insured_capital: 300,
  accidental_death_insured_capital: 0,
  itp_insured_capital: 0,
  savings_option: 'savings_capacity' as const,
  payment_periodicity: 'MONTHLY' as const,
};

export const isIdealPensionMode = (v: APVSelectFormValues) => v.savings_option === 'ideal_pension';
export const isSavingsCapacityMode = (v: APVSelectFormValues) => v.savings_option === 'savings_capacity';
export const isFixedRate = (v: APVSelectFormValues) => v.rate_mode === 'fixed';
export const isSteppedRate = (v: APVSelectFormValues) => v.rate_mode === 'stepped';

export const apvSelectDefaults: APVSelectFormValues = {
  ...sharedDefaults,
  agreed_premium: 0,
};
